<script setup lang="ts">
import { cva } from 'class-variance-authority';
import { computed } from 'vue';
import { cn } from '@/helpers/tailwind-helpers';
import BaseLabel from '@/atoms/BaseLabel.vue';
import { InputState } from '@/atoms/BaseInput.vue';

export type BaseToggleProps = {
  id: string | number;
  modelValue?: boolean;
  initialChecked?: boolean;
  class?: string;
  size?: 'medium';
  state?: InputState;
  title?: string;
  description?: string;
};

const baseToggle = cva('flex rounded-full w-14 h-8 relative mt-0.5', {
  variants: {
    state: {
      default: 'bg-grey-100 text-white',
      hover: 'bg-grey-200 text-white',
      disabled: 'bg-grey-200 text-grey-400',
      focused: '',
    },
    checked: {
      true: '',
      false: '',
    },
    size: {
      medium: '',
    },
  },
  compoundVariants: [
    { state: 'default', checked: true, class: 'bg-brand-solid' },
    { state: 'hover', checked: true, class: 'bg-blue-900' },
  ],
  defaultVariants: {
    state: 'default',
    size: 'medium',
  },
});

const props = defineProps<BaseToggleProps>();
const emit = defineEmits<{ 'update:modelValue': [value: boolean] }>();

const isChecked = computed(() => props.modelValue ?? props.initialChecked ?? false);

function handleChange(event: Event) {
  emit('update:modelValue', (event.target as HTMLInputElement).checked);
}

const classes = computed(() =>
  cn(
    baseToggle({
      ...props,
      checked: isChecked.value,
    }),
    props.class,
  ),
);
</script>

<template>
  <div class="flex space-x-2">
    <div :class="classes">
      <input
        :id="String(id)"
        :disabled="state === 'disabled'"
        type="checkbox"
        class="appearance-none w-full h-full z-10"
        :checked="isChecked"
        @change="handleChange"
      />
      <div
        class="absolute top-0 bottom-0 flex items-center justify-center"
        :class="isChecked ? 'right-0.5' : 'left-0.5'"
      >
        <div class="w-7 h-7 bg-current rounded-full"></div>
      </div>
    </div>
    <div class="flex flex-col">
      <BaseLabel v-if="title" :text="title" preset="medium" />
      <BaseLabel v-if="description" :text="description" preset="small" color="secondary" />
    </div>
  </div>
</template>
